/**
 * KULLANICININ YAZDIGI TUTAR -> SAYI.
 *
 * Sunucudaki `normalizeTaskAmount` numeric(11,2) alanina yaziyor; teklif fiyati
 * da AYNI alanda yasiyor. Talebe, teklifin ifade edemeyecegi bir tutar
 * yazilabilseydi sessizce yuvarlanan ya da reddedilen teklifler uretirdi —
 * bu yuzden tavan burada da var ve sunucununkinin AYNISI.
 */
export const MAX_TASK_AMOUNT = 999999999.99;

/**
 * tr-TR yazimi: binlik '.', ondalik ','.
 *
 * Iki aliskanlik da destekleniyor cunku ikisi de sahada goruluyor:
 *   "1.234,56" -> 1234.56   (tr: virgul ondalik)
 *   "1234.56"  -> 1234.56   (tek nokta, ardinda 1-2 hane: ondalik sayilir)
 *   "1.234"    -> 1234      (tek nokta, ardinda 3 hane: binlik sayilir)
 *
 * Son satir bilincli bir tercih ve belirsiz: "1.234" bir kullanici icin 1234,
 * bir baskasi icin 1,234 olabilir. tr-TR yazimi esas alindi — uygulamanin dili
 * bu ve ondalikli tutarlar virgulle yaziliyor.
 *
 * Cozumlenemeyen giris `null` doner; cagiran taraf hata mesajini gosterir.
 */
export const parseAmount = (input: string): number | null => {
  const trimmed = input.replace(/\s/g, '');
  if (trimmed.length === 0) return null;
  if (!/^[\d.,]+$/.test(trimmed)) return null;

  let normalized: string;
  if (trimmed.includes(',')) {
    // Virgul varsa ondalik ayirici odur; noktalar binliktir.
    normalized = trimmed.replace(/\./g, '').replace(',', '.');
  } else {
    const parts = trimmed.split('.');
    const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
    normalized =
      parts.length === 2 && last.length > 0 && last.length < 3
        ? trimmed
        : trimmed.replace(/\./g, '');
  }

  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
};

/** Tutar sunucunun kabul edecegi araliktan mi. */
export const isAmountInRange = (value: number): boolean =>
  value > 0 && value <= MAX_TASK_AMOUNT;

/* -------------------------------------------------------------------------- */
/* TEKLIF KURU                                                                 */
/* -------------------------------------------------------------------------- */

/** Teklif fiyatinin ondalik hanesi — `task_offer.offer_price numeric(11,2)`. */
export const OFFER_RATE_MAX_FRACTION_DIGITS = 2;

/**
 * `parseRate` sonucu:
 *   `{ value }`               -> gecerli kur
 *   `{ error: 'tooPrecise' }` -> sayi okunabildi ama 2'den fazla ondalik var
 *   `null`                    -> cozumlenemedi / sifir ya da negatif
 */
export type ParsedRate = { value: number } | { error: 'tooPrecise' };

/** Binlik gruplamasi gecerli mi: "1.234" / "12.345.678" evet, "12.34" hayir. */
const isGroupedThousands = (integerPart: string): boolean =>
  /^\d{1,3}(\.\d{3})+$/.test(integerPart);

/**
 * KULLANICININ YAZDIGI TEKLIF KURU -> SAYI. YALNIZ teklif fiyati icin; talep tutari
 * `parseAmount` ile okunmaya devam eder.
 *
 * NEDEN AYRI: `parseAmount("34.567")` 34567 dondurur — tek nokta + 3 hane binlik sayilir.
 * Tutarda bu makul bir tr-TR varsayimi, KURDA ise felaket: buro TCMB ekranindan "34.567"
 * kopyalar ve 1000 kat pahali bir teklif onay ekranina kadar gelir. Kurda binlik ayirici
 * pratikte hic gorulmez, ondalik ise HER ZAMAN vardir. Kurallar:
 *
 *   - Virgul varsa ondalik ayirici ODUR, noktalar binliktir ("1.234,56" -> 1234.56).
 *   - Virgul yoksa TEK nokta HER ZAMAN ondaliktir ("34.5" -> 34.5, "34.567" -> 3 hane).
 *     Birden fazla nokta ancak gecerli bir binlik gruplamasiysa kabul edilir.
 *   - 2'den fazla ondalik hane REDDEDILIR, yuvarlanmaz: sunucu da reddediyor
 *     (OFFER_PRICE_TOO_PRECISE) ve sessiz yuvarlama kullanicinin yazmadigi bir fiyati
 *     gondermek olurdu.
 */
export const parseRate = (input: string): ParsedRate | null => {
  const trimmed = input.replace(/\s/g, '');
  if (trimmed.length === 0) return null;
  if (!/^[\d.,]+$/.test(trimmed)) return null;

  let integerPart: string;
  let fractionPart: string;
  const commaCount = (trimmed.match(/,/g) ?? []).length;
  if (commaCount > 1) return null;

  if (commaCount === 1) {
    [integerPart = '', fractionPart = ''] = trimmed.split(',');
    if (integerPart.includes('.') && !isGroupedThousands(integerPart)) return null;
    integerPart = integerPart.replace(/\./g, '');
    if (fractionPart.length === 0) return null;
  } else {
    const dotCount = (trimmed.match(/\./g) ?? []).length;
    if (dotCount > 1) {
      if (!isGroupedThousands(trimmed)) return null;
      integerPart = trimmed.replace(/\./g, '');
      fractionPart = '';
    } else {
      [integerPart = '', fractionPart = ''] = trimmed.split('.');
      if (dotCount === 1 && fractionPart.length === 0) return null;
    }
  }

  if (!/^\d+$/.test(integerPart) || (fractionPart !== '' && !/^\d+$/.test(fractionPart))) {
    return null;
  }

  const value = Number(fractionPart === '' ? integerPart : `${integerPart}.${fractionPart}`);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (fractionPart.length > OFFER_RATE_MAX_FRACTION_DIGITS) return { error: 'tooPrecise' };
  return { value };
};
